import { useLocalStorage } from '@vueuse/core';

const OFFLINE_FAVORITES_KEY = 'shorts:offlineFavorites';

export interface OfflineFavoriteEntry {
	propertyId: string;
	/** Commercial id used for behavior events; may be missing on legacy entries. */
	commercialId?: string;
}

interface OfflineFavoritesData {
	/** Legacy shape — migrated to `entries` on read. */
	ids?: string[];
	entries?: OfflineFavoriteEntry[];
}

const DEFAULT_DATA: OfflineFavoritesData = { entries: [] };

function normalizeEntries(data: OfflineFavoritesData): OfflineFavoriteEntry[] {
	if (data.entries?.length) return data.entries;
	if (data.ids?.length) {
		return data.ids.map(propertyId => ({ propertyId }));
	}
	return [];
}

export function useOfflineFavorites() {
	const data = useLocalStorage<OfflineFavoritesData>(
		OFFLINE_FAVORITES_KEY,
		DEFAULT_DATA
	);

	function getEntries(): OfflineFavoriteEntry[] {
		return normalizeEntries(data.value).map(entry => ({ ...entry }));
	}

	function writeEntries(entries: OfflineFavoriteEntry[]): void {
		data.value = { entries };
	}

	function isOfflineFavorite(propertyId: string): boolean {
		return normalizeEntries(data.value).some(
			entry => entry.propertyId === propertyId
		);
	}

	function addOfflineFavorite(
		propertyId: string,
		commercialId?: string | null
	): void {
		const entries = normalizeEntries(data.value);
		if (entries.some(entry => entry.propertyId === propertyId)) return;

		const trimmedCommercialId = commercialId?.trim();
		writeEntries([
			...entries,
			{
				propertyId,
				...(trimmedCommercialId ? { commercialId: trimmedCommercialId } : {})
			}
		]);
	}

	function removeOfflineFavorite(propertyId: string): void {
		writeEntries(
			normalizeEntries(data.value).filter(
				entry => entry.propertyId !== propertyId
			)
		);
	}

	function getIds(): string[] {
		return getEntries().map(entry => entry.propertyId);
	}

	function clearAll(): void {
		writeEntries([]);
	}

	return {
		isOfflineFavorite,
		addOfflineFavorite,
		removeOfflineFavorite,
		getEntries,
		getIds,
		clearAll
	};
}
